/**
 * Client-SDK-specific specialization of the chat message document schema:
 * timestamps transform to Firestore `Timestamp` instances, and we expose
 * typed doc / collection references via `createConverter`.
 */
import {
  collection,
  doc,
  getFirestore,
  type CollectionReference,
  type DocumentReference,
  type Firestore,
} from 'firebase/firestore';
import {z} from 'zod';
import {
  StoredChatMessageSchema as StoredChatMessageBaseSchema,
  getChatMessageDocPath,
  getChatMessagesCollectionPath,
} from '../documents/chatMessage';
import type {SectionWeekId} from '../../section/sectionApi';
import {ClientTimestampSchema, createConverter} from './sdk';

/**
 * Stored chat message with client-SDK `Timestamp` instances (via
 * `ClientTimestampSchema`). Same field names as the structural schema
 * in `../documents/chatMessage`; only timestamp runtime type differs.
 *
 * `createdAt` is nullable here (even though the persisted shape always has
 * a resolved server timestamp) because Firestore's real-time listener
 * emits local-write-pending docs with `createdAt: null` while the
 * `serverTimestamp()` sentinel is in flight. Without this, `validateOnRead`
 * would reject the optimistic emission, throwing inside the render and
 * tripping `ChatErrorBoundary`. Same story for `editedAt` / `deletedAt` on
 * optimistic edit / soft-delete writes.
 */
export const StoredChatMessageSchema = StoredChatMessageBaseSchema.extend({
  createdAt: ClientTimestampSchema.nullable(),
  editedAt: ClientTimestampSchema.nullable().optional(),
  deletedAt: ClientTimestampSchema.nullable().optional(),
}).loose();

/** Inferred type (client-SDK timestamps). */
export type StoredChatMessage = z.infer<typeof StoredChatMessageSchema>;

/**
 * View-model for the UI: stored shape + `doc.id`. Not part of the schema;
 * attach via `{...doc.data(), id: doc.id}` at the call site (or the hook).
 */
export interface ChatMessage extends StoredChatMessage {
  readonly id: string;
}

/**
 * Converter enforces schema on read (so consumers always see validated
 * Timestamp instances) but not on write, because create calls pass
 * `serverTimestamp()` sentinels which aren't resolved Timestamps yet.
 */
const chatMessageConverter = createConverter({
  schema: StoredChatMessageSchema,
  options: {validateOnRead: true, validateOnWrite: false},
});

/**
 * Typed reference for a single message doc.
 *
 * @param args.sectionId - The section id.
 * @param args.sectionWeekId - The zero-indexed section week.
 * @param args.messageId - The Firestore message id.
 * @param args.firestore - Firestore instance. Defaults to `getFirestore()`.
 * @returns A `DocumentReference<ChatMessage>`.
 */
export function getChatMessageDocRef({
  sectionId,
  sectionWeekId,
  messageId,
  firestore = getFirestore(),
}: {
  sectionId: string;
  sectionWeekId: SectionWeekId | number;
  messageId: string;
  firestore?: Firestore;
}): DocumentReference<StoredChatMessage> {
  return doc(
    firestore,
    getChatMessageDocPath({sectionId, sectionWeekId, messageId}),
  ).withConverter(chatMessageConverter);
}

/**
 * Typed reference for the messages collection of a given week.
 *
 * @param args.sectionId - The section id.
 * @param args.sectionWeekId - The zero-indexed section week.
 * @param args.firestore - Firestore instance. Defaults to `getFirestore()`.
 * @returns A `CollectionReference<ChatMessage>`.
 */
export function getChatMessagesCollectionRef({
  sectionId,
  sectionWeekId,
  firestore = getFirestore(),
}: {
  sectionId: string;
  sectionWeekId: SectionWeekId | number;
  firestore?: Firestore;
}): CollectionReference<StoredChatMessage> {
  return collection(
    firestore,
    getChatMessagesCollectionPath({sectionId, sectionWeekId}),
  ).withConverter(chatMessageConverter);
}
